/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

// Component imports
import { Alert, Button, Card, Checkbox, Col, Divider, Form, Input, Row, Select, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { DeleteIcon } from '../Icons/DeleteIcon';
import Prompt from '../UI/Prompt';

// Misc imports
import useBeforeUnload from '../../utils/hooks/useBeforeUnload';
import { useFetchBotQuery, useUpdateBotMutation } from '../../store/bots/botsApi';
import { useFetchIntegrationsQuery } from '../../store/integrations/integrationsApi';
import { INTEGRATION_TYPE } from '../../constants/integrations';

const { Text, Title } = Typography;

const SecuritySettingsForm = () => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();
  const [form] = Form.useForm();

  const { data: bot, isLoading: isLoadingBot } = useFetchBotQuery({ orgId, tenantId }, { skip: !tenantId });
  const [updateBot, { isLoading: isSaving }] = useUpdateBotMutation({ fixedCacheKey: 'updateSecuritySettings' });
  const { data: integrationsData, isLoading: isLoadingIntegrations } = useFetchIntegrationsQuery(
    { orgId, urlParams: { type: INTEGRATION_TYPE.OIDC_AUTHENTICATION } },
    { skip: !orgId }
  );

  const oidcIntegrations = integrationsData?.items ?? [];

  const initialValues = useMemo(() => {
    const security = bot?.settings?.security;
    if (!security) {
      return {};
    }
    return {
      preAuthTokenProviders: security.preAuthTokenProviders ?? [],
      oidcIntegrationId: security.oidc?.integration?.id ?? null,
      requireEmailVerified: security.oidc?.requireEmailVerified ?? false,
      additionalClaims: (security.oidc?.additionalClaims ?? []).join(', ')
    };
  }, [bot]);

  const [preAuthEnabled, setPreAuthEnabled] = useState(false);
  const [oidcEnabled, setOidcEnabled] = useState(false);
  const [isFieldsTouched, setIsFieldsTouched] = useState(false);

  useEffect(() => {
    setPreAuthEnabled((initialValues.preAuthTokenProviders?.length ?? 0) > 0);
    setOidcEnabled(Boolean(initialValues.oidcIntegrationId));
    setIsFieldsTouched(false);
    form.resetFields();
  }, [initialValues, form]);

  useBeforeUnload(isFieldsTouched, t('Your unsaved changes will be lost.'));

  const handlePreAuthToggle = (e) => {
    setPreAuthEnabled(e.target.checked);
    setIsFieldsTouched(true);
  };

  const handleOidcToggle = (e) => {
    setOidcEnabled(e.target.checked);
    setIsFieldsTouched(true);
  };

  const handleRemoveProvider = (remove, name) => {
    remove(name);
  };

  const handleSave = async (values) => {
    const preAuthTokenProviders = preAuthEnabled
      ? (values.preAuthTokenProviders ?? []).filter((p) => p && p.name && p.aud && p.jwksUri)
      : [];

    const oidc = (oidcEnabled && values.oidcIntegrationId)
      ? {
        integrationId: values.oidcIntegrationId,
        requireEmailVerified: values.requireEmailVerified ?? false,
        additionalClaims: (values.additionalClaims || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      }
      : undefined;

    await updateBot({
      orgId,
      tenantId,
      data: {
        settings: {
          security: {
            preAuthTokenProviders,
            oidc
          }
        }
      }
    }).unwrap();

    setIsFieldsTouched(false);
  };

  if (isLoadingBot || isLoadingIntegrations) {
    return <Card loading />;
  }

  return (
    <Fragment>
      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFieldsChange={() => setIsFieldsTouched(true)}
          onFinish={handleSave}
        >
          <Checkbox
            checked={preAuthEnabled}
            onChange={handlePreAuthToggle}
          >
            <Title level={5} className="mb-0 d-inline">{t('Pre-Authenticated Token Providers')}</Title>
          </Checkbox>
          <Text type="secondary" className="mb-4 d-block">
            {t('Configure token providers for pre-authenticated users (e.g. SharePoint SPFx). Tokens arriving via channelData.auth.token are validated against matching providers.')}
          </Text>

          <Form.List name="preAuthTokenProviders">
            {(fields, { add, remove }) => (
              <Fragment>
                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    className={classNames('bg-layout mb-3 shadow-none position-relative', {
                      'opacity-50': !preAuthEnabled
                    })}
                  >
                    <div className="position-absolute top-0 right-0 mt-2 mr-2">
                      <Button
                        type="text"
                        shape="circle"
                        className="text-primary"
                        icon={<DeleteIcon />}
                        disabled={!preAuthEnabled}
                        onClick={() => handleRemoveProvider(remove, name)}
                      />
                    </div>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          label={t('Provider name')}
                          name={[name, 'name']}
                          rules={preAuthEnabled ? [{ required: true, message: t('Name is required') }] : []}
                          className="mb-0"
                        >
                          <Input disabled={!preAuthEnabled} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          label={t('Audience (aud)')}
                          name={[name, 'aud']}
                          rules={preAuthEnabled ? [{ required: true, message: t('Audience is required') }] : []}
                          className="mb-0"
                        >
                          <Input disabled={!preAuthEnabled} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          label={t('JWKS URI')}
                          name={[name, 'jwksUri']}
                          rules={preAuthEnabled ? [
                            { required: true, message: t('JWKS URI is required') },
                            { type: 'url', message: t('Please enter a valid URL') }
                          ] : []}
                          className="mb-0"
                        >
                          <Input disabled={!preAuthEnabled} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          label={t('Refresh Token Endpoint')}
                          name={[name, 'refreshTokenEndpoint']}
                          rules={preAuthEnabled ? [{ type: 'url', message: t('Please enter a valid URL') }] : []}
                          className="mb-0"
                        >
                          <Input disabled={!preAuthEnabled} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    disabled={!preAuthEnabled}
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                  >
                    {t('Add Provider')}
                  </Button>
                </Form.Item>
              </Fragment>
            )}
          </Form.List>

          <Divider />

          <Checkbox
            checked={oidcEnabled}
            disabled={oidcIntegrations.length === 0}
            onChange={handleOidcToggle}
          >
            <Title level={5} className="mb-0 d-inline">{t('OIDC Authentication')}</Title>
          </Checkbox>

          <Card className={classNames('bg-layout mt-3 mb-3 shadow-none', {
            'opacity-50': !oidcEnabled || oidcIntegrations.length === 0
          })}>
            {oidcIntegrations.length === 0 ? (
              <Alert
                type="info"
                showIcon
                message={t('No OIDC integrations available. Create one in Workspace > Integrations.')}
              />
            ) : (
              <Fragment>
                <Form.Item
                  name="oidcIntegrationId"
                  label={t('OIDC Integration')}
                >
                  <Select
                    allowClear
                    disabled={!oidcEnabled}
                    placeholder={t('Select an OIDC integration')}
                    options={oidcIntegrations.map((i) => ({
                      label: i.name || i.type,
                      value: i.id
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  shouldUpdate={(prev, curr) => prev.oidcIntegrationId !== curr.oidcIntegrationId}
                  noStyle
                >
                  {({ getFieldValue }) => {
                    const subFieldsEnabled = oidcEnabled && Boolean(getFieldValue('oidcIntegrationId'));
                    return (
                      <Fragment>
                        <Form.Item
                          name="additionalClaims"
                          label={t('Additional Claims')}
                          extra={t('Comma-separated list of additional token claims to extract')}
                          className="mb-2"
                        >
                          <Input
                            disabled={!subFieldsEnabled}
                            placeholder={t('e.g. preferred_username, tid, groups')}
                          />
                        </Form.Item>

                        <Form.Item
                          name="requireEmailVerified"
                          valuePropName="checked"
                          className="mb-0"
                        >
                          <Checkbox disabled={!subFieldsEnabled}>{t('Require email verification')}</Checkbox>
                        </Form.Item>
                      </Fragment>
                    );
                  }}
                </Form.Item>
              </Fragment>
            )}
          </Card>

          <Divider />

          <Row justify="end">
            <Button
              type="primary"
              loading={isSaving}
              htmlType="submit"
            >
              {t('Save')}
            </Button>
          </Row>
        </Form>
      </Card>

      <Prompt
        when={isFieldsTouched}
        onConfirm={async () => {
          const values = await form.validateFields();
          await handleSave(values);
        }}
        okText={t('Save Changes')}
        cancelText={t('Discard Changes')}
        title={t('Save Changes?')}
        message={t('Your unsaved changes will be lost. Save changes before closing?')}
      />
    </Fragment>
  );
};

export default SecuritySettingsForm;
